import { type CSSProperties, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/** Header-only height to open from: p-4 + the 44px header row + p-4. */
const COLLAPSED_PX = 76
/** Beat 2 starts just after mount; beat 3 once the growth has landed. */
const EXPAND_AT_MS = 60
const GROWTH_MS = 500
/** Beat 3: each row's own fade, and the gap between one row and the next. */
const ROW_FADE_MS = 320
const ROW_STEP_MS = 100

function prefersReducedMotion() {
  // jsdom and some embedded contexts define `window` but not `matchMedia`.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface PacedFocusRevealProps {
  children: React.ReactNode
  /**
   * Multiplier on every beat, for surfaces that want the same opening at a different
   * tempo (the marketing loop runs it faster). 1 is the product's own pace.
   */
  pace?: number
}

/**
 * The product's focus reveal, with a `pace` multiplier so the marketing loop can
 * run the card's beats faster than the real screen does. Copied here rather than
 * adding the prop to the shipped QuoteCardFocusReveal: this branch is a prototype
 * and leaves production components alone. Delete it in favour of the real one
 * once the paced version lands on main.
 */
export function PacedFocusReveal({ children, pace = 1 }: PacedFocusRevealProps) {
  const innerRef = useRef<HTMLDivElement>(null)
  // Reduced motion starts on the final frame; killing transitions alone would still snap.
  const reduced = prefersReducedMotion()
  const [height, setHeight] = useState<number | undefined>(reduced ? undefined : COLLAPSED_PX)
  const [revealed, setRevealed] = useState(reduced)

  const growthMs = GROWTH_MS * pace

  useEffect(() => {
    if (reduced) return
    const timers = [
      // Opacity doesn't affect layout, so this reads the final height while rows are hidden.
      window.setTimeout(() => {
        if (innerRef.current) setHeight(innerRef.current.offsetHeight)
      }, EXPAND_AT_MS),
      window.setTimeout(() => {
        setRevealed(true)
        setHeight(undefined)
      }, EXPAND_AT_MS + growthMs),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduced, growthMs])

  // Carried as custom properties so two instances at different paces can't fight over
  // the shared stylesheet below.
  const timing = {
    height,
    transitionDuration: `${growthMs}ms`,
    '--reveal-fade': `${ROW_FADE_MS * pace}ms`,
    '--reveal-step': `${ROW_STEP_MS * pace}ms`,
  } as CSSProperties

  return (
    <div
      className={cn(
        'transition-[height] ease-out motion-reduce:transition-none',
        height !== undefined && 'overflow-hidden'
      )}
      style={timing}
    >
      <div ref={innerRef} data-reveal={revealed ? 'on' : 'off'} className="quote-focus-reveal">
        {children}
      </div>
      <style>
        {`
          /* Reaches into QuoteCard's body to stagger the rows it owns. The first child is
             the header, held visible so beat 2 grows an empty card rather than a blank one. */
          /* visibility, not just opacity — aria-hidden alone leaves rows tabbable. */
          .quote-focus-reveal > div > div > *:not(:first-child) {
            transition: opacity var(--reveal-fade) ease-out, visibility var(--reveal-fade);
          }
          /* Only the hidden state sets opacity. Declaring the revealed state as opacity:1
             outranked Tailwind's disabled:opacity-50, so disabled CTAs looked enabled. */
          .quote-focus-reveal:not([data-reveal='on']) > div > div > *:not(:first-child) {
            opacity: 0;
            visibility: hidden;
          }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(2) { transition-delay: 0ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(3) { transition-delay: calc(var(--reveal-step) * 1); }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(4) { transition-delay: calc(var(--reveal-step) * 2); }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(5) { transition-delay: calc(var(--reveal-step) * 3); }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(6) { transition-delay: calc(var(--reveal-step) * 4); }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(n + 7) { transition-delay: calc(var(--reveal-step) * 5); }

          @media (prefers-reduced-motion: reduce) {
            .quote-focus-reveal > div > div > *:not(:first-child) { transition: none; }
          }
        `}
      </style>
    </div>
  )
}
